import React, { useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  ScrollView,
  Platform,
  Switch } from 'react-native'
import {
  Store, ShoppingCart, Handshake, Building2, ChevronRight, ArrowLeft,
  MapPin, Lightbulb, LucideIcon, Users, Package, Clock, Phone, Mail,
  Globe, CreditCard, Banknote, Smartphone, Check
} from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import LocationPicker from '../components/LocationPicker'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

// Store/Merchant variant colors (Terracotta theme)
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'CreateStore'>
  route: {
    params?: {
      filterType?: 'flea_market' | 'store'
    }
  }
}

type StoreType = 'self_service_flea_market' | 'full_service_flea_market' | 'consignment_store' | 'retail_store'
type SellingModel = 'booth_based' | 'inventory_based'
type PayoutSchedule = 'per_sale' | 'weekly' | 'monthly'
type PaymentMethod = 'cash' | 'card' | 'mobilepay' | 'bank_transfer'
type OnboardingStep = 'type' | 'basic' | 'business' | 'operations' | 'review'

interface StoreTypeOption {
  value: StoreType
  icon: LucideIcon
  hasCommission: boolean
  hasSpaceRental: boolean
  defaultCommission: number
  hasSellingModel?: boolean
  hasConsignmentPeriod?: boolean
  hasPayoutSchedule?: boolean
}

const STORE_TYPES: StoreTypeOption[] = [
  {
    value: 'self_service_flea_market',
    icon: Store,
    hasCommission: true,
    hasSpaceRental: true,
    defaultCommission: 0,
    hasPayoutSchedule: true },
  {
    value: 'full_service_flea_market',
    icon: ShoppingCart,
    hasCommission: true,
    hasSpaceRental: true,
    defaultCommission: 20,
    hasSellingModel: true,
    hasPayoutSchedule: true },
  {
    value: 'consignment_store',
    icon: Handshake,
    hasCommission: true,
    hasSpaceRental: false,
    defaultCommission: 30,
    hasConsignmentPeriod: true,
    hasPayoutSchedule: true },
  {
    value: 'retail_store',
    icon: Building2,
    hasCommission: false,
    hasSpaceRental: false,
    defaultCommission: 0 },
]

const ITEM_CATEGORIES = [
  'clothing', 'furniture', 'electronics', 'books', 'toys',
  'sports', 'home_decor', 'antiques', 'jewelry', 'other'
]

const STEPS: OnboardingStep[] = ['type', 'basic', 'business', 'operations', 'review']

export default function CreateStoreScreen({ navigation, route }: Props) {
  const { t } = useLanguage()
  const { user } = useAuth()
  const filterType = route.params?.filterType

  // Step management
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('type')

  // Step 1: Type
  const [storeType, setStoreType] = useState<StoreType | null>(null)

  // Step 2: Basic Info
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)

  // Step 3: Business Settings
  const [commissionRate, setCommissionRate] = useState('0')
  const [sellingModel, setSellingModel] = useState<SellingModel>('booth_based')
  const [payoutSchedule, setPayoutSchedule] = useState<PayoutSchedule>('weekly')
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(['cash', 'card'])
  const [consignmentPeriodDays, setConsignmentPeriodDays] = useState('30')
  const [acceptsOffers, setAcceptsOffers] = useState(false)

  // Step 4: Operations
  const [isEventBased, setIsEventBased] = useState(false)
  const [acceptedCategories, setAcceptedCategories] = useState<string[]>([])
  const [minItemValue, setMinItemValue] = useState('')
  const [sellerTerms, setSellerTerms] = useState('')

  const [loading, setLoading] = useState(false)

  const selectedTypeConfig = STORE_TYPES.find(t => t.value === storeType)
  const isFleaMarketMode = filterType === 'flea_market'
  const currentStepIndex = STEPS.indexOf(currentStep)

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const handleTypeSelect = (type: StoreType) => {
    setStoreType(type)
    const config = STORE_TYPES.find(t => t.value === type)
    if (config) {
      setCommissionRate(config.defaultCommission.toString())
    }
    setCurrentStep('basic')
  }

  const goToStep = (step: OnboardingStep) => {
    setCurrentStep(step)
  }

  const goBack = () => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex])
    } else {
      navigation.goBack()
    }
  }

  const goNext = () => {
    // Validate current step
    if (currentStep === 'basic') {
      if (!name.trim()) {
        showAlert(t('error'), t('enter_store_name'))
        return
      }
    }

    const nextIndex = currentStepIndex + 1
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex])
    }
  }

  const togglePaymentMethod = (method: PaymentMethod) => {
    setPaymentMethods(prev =>
      prev.includes(method)
        ? prev.filter(m => m !== method)
        : [...prev, method]
    )
  }

  const toggleCategory = (category: string) => {
    setAcceptedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }

  const createStore = async () => {
    if (!name.trim()) {
      showAlert(t('error'), t('enter_store_name'))
      return
    }

    if (!user?.id) {
      showAlert(t('error'), t('login_required'))
      return
    }

    if (!storeType) {
      showAlert(t('error'), 'Please select a store type')
      return
    }

    setLoading(true)
    try {
      const commission = selectedTypeConfig?.hasCommission ? parseInt(commissionRate) || 0 : 0

      // Build metadata object with all fields
      const metadata: Record<string, any> = {
        // Contact info
        phone: phone.trim() || null,
        email: email.trim() || null,
        website: website.trim() || null,

        // Address
        address: address.trim() || null,
        postal_code: postalCode.trim() || null,

        // Payment
        payment_methods: paymentMethods,

        // Platform provision (fixed)
        platform_provision: 5 }

      // Type-specific fields
      if (selectedTypeConfig?.hasSellingModel) {
        metadata.selling_model = sellingModel
      }
      if (selectedTypeConfig?.hasConsignmentPeriod) {
        metadata.consignment_period_days = parseInt(consignmentPeriodDays) || 30
      }
      if (selectedTypeConfig?.hasPayoutSchedule) {
        metadata.payout_schedule = payoutSchedule
      }
      if (storeType === 'retail_store' || storeType === 'consignment_store') {
        metadata.accepts_offers = acceptsOffers
      }

      // Operations
      if (storeType === 'self_service_flea_market' || storeType === 'full_service_flea_market') {
        metadata.is_event_based = isEventBased
      }
      if (acceptedCategories.length > 0) {
        metadata.accepted_categories = acceptedCategories
      }
      if (minItemValue) {
        metadata.min_item_value = parseFloat(minItemValue) || null
      }
      if (sellerTerms.trim()) {
        metadata.seller_terms = sellerTerms.trim()
      }

      const { data: storeId, error } = await supabase.rpc('create_store', {
        p_name: name.trim(),
        p_store_type: storeType,
        p_description: description.trim() || null,
        p_city: city.trim() || null,
        p_commission_percentage: commission,
        p_metadata: metadata,
        p_latitude: latitude,
        p_longitude: longitude })

      if (error) throw error

      showAlert(t('success'), isFleaMarketMode ? t('market_created') : t('store_created'))

      // Navigate to MarketAdmin for flea markets (to set up spaces), MerchantDashboard for stores
      if (isFleaMarketMode) {
        navigation.replace('MarketAdmin', { marketId: storeId })
      } else {
        navigation.replace('MerchantDashboard', { storeId })
      }
    } catch (error: any) {
      console.error('Create store error:', error)
      showAlert(t('error'), error.message || 'Failed to create store')
    } finally {
      setLoading(false)
    }
  }

  // Filter store types based on filterType param
  const displayTypes = filterType === 'flea_market'
    ? STORE_TYPES.filter(t => t.value === 'self_service_flea_market' || t.value === 'full_service_flea_market')
    : filterType === 'store'
    ? STORE_TYPES.filter(t => t.value === 'consignment_store' || t.value === 'retail_store')
    : STORE_TYPES

  // Step indicator
  const renderStepIndicator = () => {
    if (currentStep === 'type') return null

    const visibleSteps = STEPS.filter(s => s !== 'type')
    const activeIndex = visibleSteps.indexOf(currentStep)

    return (
      <View style={styles.stepIndicator}>
        {visibleSteps.map((step, index) => (
          <View key={step} style={styles.stepDot}>
            <View style={[
              styles.dot,
              index <= activeIndex && styles.dotActive
            ]}>
              {index < activeIndex && <Check size={12} color="#fff" />}
            </View>
            {index < visibleSteps.length - 1 && (
              <View style={[
                styles.stepLine,
                index < activeIndex && styles.stepLineActive
              ]} />
            )}
          </View>
        ))}
      </View>
    )
  }

  // Step 1: Type Selection
  const renderTypeSelection = () => (
    <View style={styles.typeContainer}>
      <Text style={styles.stepTitle}>
        {isFleaMarketMode ? (t('flea_market_type') || 'Flea Market Type') : t('store_type')}
      </Text>
      <Text style={styles.stepSubtitle}>
        {isFleaMarketMode
          ? (t('select_flea_market_type') || 'What type of flea market are you creating?')
          : (t('select_store_type') || 'What type of store are you creating?')}
      </Text>

      <View style={styles.typeList}>
        {displayTypes.map((type) => {
          const IconComponent = type.icon
          return (
            <TouchableOpacity
              key={type.value}
              style={styles.typeCard}
              onPress={() => handleTypeSelect(type.value)}
              activeOpacity={0.7}
            >
              <View style={styles.typeIconContainer}>
                <IconComponent size={28} color={merchantColors.primary} />
              </View>
              <View style={styles.typeInfo}>
                <Text style={styles.typeName}>{t(type.value)}</Text>
                <Text style={styles.typeDesc}>{t(`${type.value}_desc`)}</Text>
              </View>
              <ChevronRight size={20} color={merchantColors.primary} />
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )

  // Step 2: Basic Info
  const renderBasicInfo = () => (
    <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
      <Text style={styles.sectionTitle}>{t('basic_info')}</Text>
      <Text style={styles.sectionSubtitle}>{t('basic_info_hint') || 'Tell us about your store'}</Text>

      {/* Store Name */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('store_name')} *</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder={t('store_name_placeholder')}
          placeholderTextColor="#999"
        />
      </View>

      {/* Description */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('description')}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={description}
          onChangeText={setDescription}
          placeholder={t('store_description_placeholder')}
          placeholderTextColor="#999"
          multiline
          numberOfLines={4}
        />
      </View>

      <Text style={styles.sectionTitle}>{t('location')}</Text>

      {/* Address */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('street_address')}</Text>
        <TextInput
          style={styles.input}
          value={address}
          onChangeText={setAddress}
          placeholder={t('street_address_placeholder') || 'e.g. Mannerheimintie 1'}
          placeholderTextColor="#999"
        />
      </View>

      {/* City & Postal Code Row */}
      <View style={styles.row}>
        <View style={[styles.inputGroup, { flex: 2, marginRight: 12 }]}>
          <Text style={styles.label}>{t('city')}</Text>
          <TextInput
            style={styles.input}
            value={city}
            onChangeText={setCity}
            placeholder={t('city_placeholder')}
            placeholderTextColor="#999"
          />
        </View>
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={styles.label}>{t('postal_code')}</Text>
          <TextInput
            style={styles.input}
            value={postalCode}
            onChangeText={setPostalCode}
            placeholder="00100"
            placeholderTextColor="#999"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Location Pin Picker */}
      {(address.trim() || city.trim()) && (
        <LocationPicker
          address={address}
          city={city}
          initialLat={latitude}
          initialLng={longitude}
          onLocationChange={(lat, lng) => {
            setLatitude(lat)
            setLongitude(lng)
          }}
        />
      )}

      <Text style={styles.sectionTitle}>{t('contact')}</Text>

      {/* Phone */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('phone')}</Text>
        <View style={styles.iconInput}>
          <Phone size={18} color="#999" />
          <TextInput
            style={styles.iconInputField}
            value={phone}
            onChangeText={setPhone}
            placeholder="+358 40 123 4567"
            placeholderTextColor="#999"
            keyboardType="phone-pad"
          />
        </View>
      </View>

      {/* Email */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('email')}</Text>
        <View style={styles.iconInput}>
          <Mail size={18} color="#999" />
          <TextInput
            style={styles.iconInputField}
            value={email}
            onChangeText={setEmail}
            placeholder="store@example.com"
            placeholderTextColor="#999"
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>
      </View>

      {/* Website */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('website')}</Text>
        <View style={styles.iconInput}>
          <Globe size={18} color="#999" />
          <TextInput
            style={styles.iconInputField}
            value={website}
            onChangeText={setWebsite}
            placeholder="https://www.example.com"
            placeholderTextColor="#999"
            keyboardType="url"
            autoCapitalize="none"
          />
        </View>
      </View>

      <View style={styles.bottomSpacer} />
    </ScrollView>
  )

  // Step 3: Business Settings
  const renderBusinessSettings = () => (
    <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
      <Text style={styles.sectionTitle}>{t('business_settings')}</Text>
      <Text style={styles.sectionSubtitle}>{t('business_settings_hint') || 'Configure your pricing and payments'}</Text>

      {/* Selling Model - for full_service_flea_market */}
      {selectedTypeConfig?.hasSellingModel && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('selling_model')}</Text>
          <Text style={styles.fieldHint}>{t('selling_model_hint')}</Text>
          <View style={styles.modelOptions}>
            <TouchableOpacity
              style={[
                styles.modelOption,
                sellingModel === 'booth_based' && styles.modelOptionSelected
              ]}
              onPress={() => setSellingModel('booth_based')}
            >
              <View style={[styles.modelIconContainer, sellingModel === 'booth_based' && styles.modelIconSelected]}>
                <Users size={24} color={sellingModel === 'booth_based' ? '#fff' : merchantColors.primary} />
              </View>
              <Text style={[styles.modelTitle, sellingModel === 'booth_based' && styles.modelTitleSelected]}>
                {t('booth_based')}
              </Text>
              <Text style={styles.modelDesc}>{t('booth_based_desc')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.modelOption,
                sellingModel === 'inventory_based' && styles.modelOptionSelected
              ]}
              onPress={() => setSellingModel('inventory_based')}
            >
              <View style={[styles.modelIconContainer, sellingModel === 'inventory_based' && styles.modelIconSelected]}>
                <Package size={24} color={sellingModel === 'inventory_based' ? '#fff' : merchantColors.primary} />
              </View>
              <Text style={[styles.modelTitle, sellingModel === 'inventory_based' && styles.modelTitleSelected]}>
                {t('inventory_based')}
              </Text>
              <Text style={styles.modelDesc}>{t('inventory_based_desc')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Commission breakdown */}
      {selectedTypeConfig?.hasCommission && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('commission_breakdown')}</Text>

          {/* Platform Provision - fixed 5% */}
          <View style={styles.provisionRow}>
            <Text style={styles.provisionLabel}>{t('platform_provision')}</Text>
            <Text style={styles.provisionValue}>5%</Text>
          </View>
          <Text style={styles.provisionHint}>{t('platform_provision_hint')}</Text>

          {/* Market Commission - editable */}
          <View style={[styles.provisionRow, { marginTop: 12 }]}>
            <Text style={styles.provisionLabel}>{t('market_commission')}</Text>
            <View style={styles.commissionRow}>
              <TextInput
                style={[styles.input, styles.commissionInput]}
                value={commissionRate}
                onChangeText={setCommissionRate}
                keyboardType="numeric"
                placeholder="20"
                placeholderTextColor="#999"
              />
              <Text style={styles.commissionPercent}>%</Text>
            </View>
          </View>
          <Text style={styles.provisionHint}>{t('market_commission_hint')}</Text>

          {/* Total */}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('total_provision')}</Text>
            <Text style={styles.totalValue}>{5 + (parseInt(commissionRate) || 0)}%</Text>
          </View>
          <Text style={styles.totalHint}>{t('total_provision_hint')}</Text>
        </View>
      )}

      {/* Consignment Period */}
      {selectedTypeConfig?.hasConsignmentPeriod && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('consignment_period')}</Text>
          <View style={styles.commissionRow}>
            <TextInput
              style={[styles.input, styles.commissionInput]}
              value={consignmentPeriodDays}
              onChangeText={setConsignmentPeriodDays}
              keyboardType="numeric"
              placeholder="30"
              placeholderTextColor="#999"
            />
            <Text style={styles.commissionPercent}>{t('days')}</Text>
          </View>
          <Text style={styles.fieldHint}>{t('consignment_period_hint')}</Text>
        </View>
      )}

      {/* Payout Schedule */}
      {selectedTypeConfig?.hasPayoutSchedule && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('payout_schedule')}</Text>
          <View style={styles.scheduleOptions}>
            {(['per_sale', 'weekly', 'monthly'] as PayoutSchedule[]).map((schedule) => (
              <TouchableOpacity
                key={schedule}
                style={[
                  styles.scheduleOption,
                  payoutSchedule === schedule && styles.scheduleOptionSelected
                ]}
                onPress={() => setPayoutSchedule(schedule)}
              >
                <Clock size={16} color={payoutSchedule === schedule ? '#fff' : merchantColors.primary} />
                <Text style={[
                  styles.scheduleText,
                  payoutSchedule === schedule && styles.scheduleTextSelected
                ]}>
                  {t(schedule)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.fieldHint}>{t('payout_schedule_hint')}</Text>
        </View>
      )}

      {/* Payment Methods */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('payment_methods')}</Text>
        <Text style={styles.fieldHint}>{t('payment_methods_hint') || 'Select accepted payment methods'}</Text>
        <View style={styles.paymentGrid}>
          {[
            { key: 'cash' as PaymentMethod, icon: Banknote, label: t('cash') || 'Cash' },
            { key: 'card' as PaymentMethod, icon: CreditCard, label: t('card') || 'Card' },
            { key: 'mobilepay' as PaymentMethod, icon: Smartphone, label: 'MobilePay' },
            { key: 'bank_transfer' as PaymentMethod, icon: Building2, label: t('bank_transfer') || 'Bank Transfer' },
          ].map(({ key, icon: Icon, label }) => (
            <TouchableOpacity
              key={key}
              style={[
                styles.paymentOption,
                paymentMethods.includes(key) && styles.paymentOptionSelected
              ]}
              onPress={() => togglePaymentMethod(key)}
            >
              <Icon size={20} color={paymentMethods.includes(key) ? merchantColors.primary : '#999'} />
              <Text style={[
                styles.paymentLabel,
                paymentMethods.includes(key) && styles.paymentLabelSelected
              ]}>{label}</Text>
              {paymentMethods.includes(key) && (
                <View style={styles.checkBadge}>
                  <Check size={12} color="#fff" />
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Accept Sales Offers */}
      {(storeType === 'retail_store' || storeType === 'consignment_store') && (
        <View style={styles.inputGroup}>
          <View style={styles.switchRow}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>{t('accepts_consignment')}</Text>
              <Text style={styles.switchHint}>{t('accepts_consignment_hint')}</Text>
            </View>
            <Switch
              value={acceptsOffers}
              onValueChange={setAcceptsOffers}
              trackColor={{ false: '#ddd', true: merchantColors.primary }}
              thumbColor="#fff"
            />
          </View>
        </View>
      )}

      <View style={styles.bottomSpacer} />
    </ScrollView>
  )

  // Step 4: Operations
  const renderOperations = () => (
    <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
      <Text style={styles.sectionTitle}>{t('operations')}</Text>
      <Text style={styles.sectionSubtitle}>{t('operations_hint') || 'How does your store operate?'}</Text>

      {/* Event-based toggle for flea markets */}
      {(storeType === 'self_service_flea_market' || storeType === 'full_service_flea_market') && (
        <View style={styles.inputGroup}>
          <View style={styles.switchRow}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>{t('event_based')}</Text>
              <Text style={styles.switchHint}>{t('event_based_hint') || 'This is a one-time or recurring event, not a permanent location'}</Text>
            </View>
            <Switch
              value={isEventBased}
              onValueChange={setIsEventBased}
              trackColor={{ false: '#ddd', true: merchantColors.primary }}
              thumbColor="#fff"
            />
          </View>
        </View>
      )}

      {/* Space Rental hint for flea markets */}
      {selectedTypeConfig?.hasSpaceRental && (storeType !== 'full_service_flea_market' || sellingModel === 'booth_based') && (
        <View style={styles.featureBox}>
          <View style={styles.featureIconContainer}>
            <MapPin size={20} color={theme.colors.brand.primary} />
          </View>
          <View style={styles.featureInfo}>
            <Text style={styles.featureTitle}>{t('space_rental')}</Text>
            <Text style={styles.featureDesc}>{t('space_rental_setup_hint') || 'You can configure spaces and pricing after creating the market'}</Text>
          </View>
        </View>
      )}

      {/* Accepted Categories for stores */}
      {(storeType === 'consignment_store' || storeType === 'retail_store') && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('accepted_categories')}</Text>
          <Text style={styles.fieldHint}>{t('accepted_categories_hint') || 'What types of items do you accept?'}</Text>
          <View style={styles.categoryGrid}>
            {ITEM_CATEGORIES.map((category) => (
              <TouchableOpacity
                key={category}
                style={[
                  styles.categoryChip,
                  acceptedCategories.includes(category) && styles.categoryChipSelected
                ]}
                onPress={() => toggleCategory(category)}
              >
                <Text style={[
                  styles.categoryText,
                  acceptedCategories.includes(category) && styles.categoryTextSelected
                ]}>
                  {t(category) || category}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Minimum Item Value */}
      {(storeType === 'consignment_store' || storeType === 'retail_store') && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('min_item_value')}</Text>
          <View style={styles.commissionRow}>
            <TextInput
              style={[styles.input, styles.commissionInput]}
              value={minItemValue}
              onChangeText={setMinItemValue}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor="#999"
            />
            <Text style={styles.commissionPercent}>€</Text>
          </View>
          <Text style={styles.fieldHint}>{t('min_item_value_hint') || 'Minimum value for items you accept (0 for no minimum)'}</Text>
        </View>
      )}

      {/* Seller Terms */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('seller_terms')}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={sellerTerms}
          onChangeText={setSellerTerms}
          placeholder={t('seller_terms_placeholder') || 'Enter any terms or conditions for sellers...'}
          placeholderTextColor="#999"
          multiline
          numberOfLines={4}
        />
        <Text style={styles.fieldHint}>{t('seller_terms_hint') || 'Rules and conditions sellers must agree to'}</Text>
      </View>

      <View style={styles.bottomSpacer} />
    </ScrollView>
  )

  // Step 5: Review
  const renderReview = () => (
    <ScrollView style={styles.form}>
      <Text style={styles.sectionTitle}>{t('review')}</Text>
      <Text style={styles.sectionSubtitle}>{t('review_hint') || 'Review your store details before creating'}</Text>

      {/* Type Badge */}
      <View style={styles.selectedTypeBadge}>
        <View style={styles.selectedTypeIconContainer}>
          {selectedTypeConfig && <selectedTypeConfig.icon size={20} color={merchantColors.primary} />}
        </View>
        <Text style={styles.selectedTypeName}>{t(storeType!)}</Text>
        <TouchableOpacity onPress={() => goToStep('type')}>
          <Text style={styles.changeType}>{t('change')}</Text>
        </TouchableOpacity>
      </View>

      {/* Basic Info Summary */}
      <View style={styles.reviewSection}>
        <View style={styles.reviewHeader}>
          <Text style={styles.reviewTitle}>{t('basic_info')}</Text>
          <TouchableOpacity onPress={() => goToStep('basic')}>
            <Text style={styles.editLink}>{t('edit')}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.reviewItem}>
          <Text style={styles.reviewLabel}>{t('store_name')}</Text>
          <Text style={styles.reviewValue}>{name || '-'}</Text>
        </View>
        {address && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('address')}</Text>
            <Text style={styles.reviewValue}>{address}, {postalCode} {city}</Text>
          </View>
        )}
        {phone && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('phone')}</Text>
            <Text style={styles.reviewValue}>{phone}</Text>
          </View>
        )}
        {email && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('email')}</Text>
            <Text style={styles.reviewValue}>{email}</Text>
          </View>
        )}
      </View>

      {/* Business Settings Summary */}
      <View style={styles.reviewSection}>
        <View style={styles.reviewHeader}>
          <Text style={styles.reviewTitle}>{t('business_settings')}</Text>
          <TouchableOpacity onPress={() => goToStep('business')}>
            <Text style={styles.editLink}>{t('edit')}</Text>
          </TouchableOpacity>
        </View>
        {selectedTypeConfig?.hasCommission && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('total_provision')}</Text>
            <Text style={styles.reviewValue}>{5 + (parseInt(commissionRate) || 0)}%</Text>
          </View>
        )}
        {selectedTypeConfig?.hasSellingModel && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('selling_model')}</Text>
            <Text style={styles.reviewValue}>{t(sellingModel)}</Text>
          </View>
        )}
        {selectedTypeConfig?.hasPayoutSchedule && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('payout_schedule')}</Text>
            <Text style={styles.reviewValue}>{t(payoutSchedule)}</Text>
          </View>
        )}
        <View style={styles.reviewItem}>
          <Text style={styles.reviewLabel}>{t('payment_methods')}</Text>
          <Text style={styles.reviewValue}>{paymentMethods.map(m => t(m) || m).join(', ')}</Text>
        </View>
      </View>

      {/* Operations Summary */}
      <View style={styles.reviewSection}>
        <View style={styles.reviewHeader}>
          <Text style={styles.reviewTitle}>{t('operations')}</Text>
          <TouchableOpacity onPress={() => goToStep('operations')}>
            <Text style={styles.editLink}>{t('edit')}</Text>
          </TouchableOpacity>
        </View>
        {(storeType === 'self_service_flea_market' || storeType === 'full_service_flea_market') && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('event_based')}</Text>
            <Text style={styles.reviewValue}>{isEventBased ? t('yes') : t('no')}</Text>
          </View>
        )}
        {acceptedCategories.length > 0 && (
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>{t('accepted_categories')}</Text>
            <Text style={styles.reviewValue}>{acceptedCategories.map(c => t(c) || c).join(', ')}</Text>
          </View>
        )}
      </View>

      {/* Create Button */}
      <TouchableOpacity
        style={[styles.createButton, loading && styles.buttonDisabled]}
        onPress={createStore}
        disabled={loading}
      >
        {loading ? (
          <Spinner color="#fff" />
        ) : (
          <Text style={styles.createButtonText}>{isFleaMarketMode ? t('create_market') : t('create_store')}</Text>
        )}
      </TouchableOpacity>

      <View style={styles.bottomSpacer} />
    </ScrollView>
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'type': return renderTypeSelection()
      case 'basic': return renderBasicInfo()
      case 'business': return renderBusinessSettings()
      case 'operations': return renderOperations()
      case 'review': return renderReview()
      default: return null
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButtonContainer} onPress={goBack}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('back')}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{isFleaMarketMode ? t('create_market') : t('create_store')}</Text>
        <View style={{ width: 50 }} />
      </View>

      {renderStepIndicator()}
      {renderCurrentStep()}

      {/* Bottom navigation buttons */}
      {currentStep !== 'type' && currentStep !== 'review' && (
        <View style={styles.bottomNav}>
          <TouchableOpacity style={styles.nextButton} onPress={goNext}>
            <Text style={styles.nextButtonText}>{t('continue')}</Text>
            <ChevronRight size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  // Step indicator
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  stepDot: {
    flexDirection: 'row',
    alignItems: 'center' },
  dot: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.colors.ui.border,
    justifyContent: 'center',
    alignItems: 'center' },
  dotActive: {
    backgroundColor: merchantColors.primary },
  stepLine: {
    width: 40,
    height: 2,
    backgroundColor: theme.colors.ui.border,
    marginHorizontal: 4 },
  stepLineActive: {
    backgroundColor: merchantColors.primary },
  // Type selection
  typeContainer: {
    flex: 1,
    padding: theme.spacing.lg },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm },
  stepSubtitle: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xl },
  typeList: {
    gap: 12 },
  typeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    ...theme.shadows.soft },
  typeIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md },
  typeInfo: {
    flex: 1 },
  typeName: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  typeDesc: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    lineHeight: 20 },
  // Form styles
  form: {
    flex: 1,
    padding: theme.spacing.md },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: 4,
    marginTop: theme.spacing.md },
  sectionSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.lg },
  inputGroup: {
    marginBottom: theme.spacing.lg },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm },
  input: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  textArea: {
    height: 100,
    textAlignVertical: 'top' },
  row: {
    flexDirection: 'row' },
  iconInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: 14,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  iconInputField: {
    flex: 1,
    padding: 14,
    fontSize: 16,
    color: theme.colors.text.primary },
  fieldHint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6 },
  // Commission styles
  commissionRow: {
    flexDirection: 'row',
    alignItems: 'center' },
  commissionInput: {
    width: 100,
    textAlign: 'center' },
  commissionPercent: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginLeft: 10 },
  provisionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8 },
  provisionLabel: {
    fontSize: 15,
    color: theme.colors.text.primary },
  provisionValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.secondary },
  provisionHint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  totalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: merchantColors.primary },
  totalHint: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  // Selling model styles
  modelOptions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: theme.spacing.md },
  modelOption: {
    flex: 1,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    alignItems: 'center' },
  modelOptionSelected: {
    borderColor: merchantColors.primary,
    backgroundColor: `${merchantColors.primary}10` },
  modelIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing.sm },
  modelIconSelected: {
    backgroundColor: merchantColors.primary },
  modelTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4,
    textAlign: 'center' },
  modelTitleSelected: {
    color: merchantColors.primary },
  modelDesc: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    lineHeight: 16 },
  // Schedule styles
  scheduleOptions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: theme.spacing.sm },
  scheduleOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  scheduleOptionSelected: {
    borderColor: merchantColors.primary,
    backgroundColor: merchantColors.primary },
  scheduleText: {
    fontSize: 13,
    fontWeight: '500',
    color: theme.colors.text.primary },
  scheduleTextSelected: {
    color: '#fff' },
  // Payment methods
  paymentGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: theme.spacing.sm },
  paymentOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  paymentOptionSelected: {
    borderColor: merchantColors.primary,
    backgroundColor: `${merchantColors.primary}10` },
  paymentLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  paymentLabelSelected: {
    color: merchantColors.primary,
    fontWeight: '500' },
  checkBadge: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: merchantColors.primary,
    justifyContent: 'center',
    alignItems: 'center' },
  // Switch row
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  switchInfo: {
    flex: 1,
    marginRight: theme.spacing.md },
  switchLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  switchHint: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  // Feature box
  featureBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: `${theme.colors.brand.primary}15`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg },
  featureIconContainer: {
    marginRight: theme.spacing.md },
  featureInfo: {
    flex: 1 },
  featureTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.brand.primary,
    marginBottom: 4 },
  featureDesc: {
    fontSize: 13,
    color: theme.colors.brand.primary,
    lineHeight: 18 },
  // Category grid
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: theme.spacing.sm },
  categoryChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.background.card,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  categoryChipSelected: {
    borderColor: merchantColors.primary,
    backgroundColor: `${merchantColors.primary}15` },
  categoryText: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  categoryTextSelected: {
    color: merchantColors.primary,
    fontWeight: '500' },
  // Review styles
  selectedTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${merchantColors.primary}15`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg },
  selectedTypeIconContainer: {
    marginRight: 10 },
  selectedTypeName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: merchantColors.primary },
  changeType: {
    fontSize: 14,
    color: merchantColors.primary,
    fontWeight: '500' },
  reviewSection: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  reviewTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  editLink: {
    fontSize: 14,
    color: merchantColors.primary,
    fontWeight: '500' },
  reviewItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8 },
  reviewLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  reviewValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary,
    textAlign: 'right',
    flex: 1,
    marginLeft: 16 },
  // Bottom navigation
  bottomNav: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    gap: 8 },
  nextButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  createButton: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginTop: 10,
    ...theme.shadows.soft },
  buttonDisabled: {
    opacity: 0.6 },
  createButtonText: {
    color: theme.colors.text.inverse,
    fontSize: 18,
    fontWeight: '600' },
  bottomSpacer: {
    height: 40 } })
